import { ApolloError } from '@apollo/client';
import { useMemo } from 'react';

import {
  EditSummaryTrowser_DocumentFragment,
  EditSummaryTrowser_DocumentSummaryFragment,
  EditSummaryTrowser_FilePageFragment,
  useEditSummaryTrowserQuery,
} from '../graphql/EditSummaryTrowser.generated';

interface UseEditSummaryTrowserDataResult {
  document: EditSummaryTrowser_DocumentFragment | null;
  documentFilePages: EditSummaryTrowser_FilePageFragment[];
  documentSummary: { id: string; summary: string } | null;
  loading: boolean;
  error: ApolloError | undefined;
}

export function useEditSummaryTrowserData(
  documentId: string,
  summaryId: string | null
): UseEditSummaryTrowserDataResult {
  const { data, loading, error } = useEditSummaryTrowserQuery({
    variables: {
      summaryID: summaryId!,
      documentID: documentId,
      includeSummary: !!summaryId,
    },
  });

  const summaryResult =
    data?.summary as EditSummaryTrowser_DocumentSummaryFragment | null;

  const documentResult =
    data?.document as EditSummaryTrowser_DocumentFragment | null;

  const result = useMemo((): UseEditSummaryTrowserDataResult => {
    // not having a summary is an okay state;
    // we may not have a summary yet or it may have been deleted
    if (!documentResult) {
      return {
        document: null,
        documentFilePages: [],
        documentSummary: null,
        loading,
        error,
      };
    }

    const documentFilePages = documentResult.pdfFile?.pages || [];
    const documentSummary = summaryResult || null;

    return {
      document: documentResult,
      documentFilePages,
      documentSummary,
      loading,
      error,
    };
  }, [documentResult, summaryResult, loading, error]);

  return result;
}
